import React, { useState } from 'react';
import './Register.scss';

// Public identifiers of the eflow-marketplace Cognito pool (us-east-2_B9XCLnPh3).
const COGNITO_ENDPOINT = 'https://cognito-idp.us-east-2.amazonaws.com/';
const COGNITO_CLIENT_ID = '1rei6lh1l8hvk61biqlm0ijadb';
const SUPPORT_EMAIL = 'support@eflow.app';

const FIELDS = [
	{ name: 'companyName', label: 'Company name', type: 'text', autoComplete: 'organization' },
	{ name: 'contactPerson', label: 'Contact person', type: 'text', autoComplete: 'name' },
	{ name: 'email', label: 'Work email', type: 'email', autoComplete: 'email' },
	{ name: 'contactPhone', label: 'Phone', type: 'tel', autoComplete: 'tel', pattern: '\\+?[0-9 ]{6,20}', title: 'Digits only, optionally starting with +' },
	{ name: 'countryName', label: 'Country', type: 'text', autoComplete: 'country-name' },
	{ name: 'cityName', label: 'City', type: 'text', autoComplete: 'address-level2' },
];

const EMPTY_FORM = Object.fromEntries(FIELDS.map((f) => [f.name, '']));

// Nobody signs in to this pool; the password only satisfies Cognito's policy.
const randomPassword = () => {
	const bytes = crypto.getRandomValues(new Uint8Array(24));
	return 'Aa1!' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const signUp = async (form, token) => {
	const response = await fetch(COGNITO_ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-amz-json-1.1',
			'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
		},
		body: JSON.stringify({
			ClientId: COGNITO_CLIENT_ID,
			Username: form.email,
			Password: randomPassword(),
			UserAttributes: [
				{ Name: 'email', Value: form.email },
				{ Name: 'name', Value: form.contactPerson },
				{ Name: 'custom:company', Value: form.companyName },
				{ Name: 'custom:phone', Value: form.contactPhone },
				{ Name: 'custom:country', Value: form.countryName },
				{ Name: 'custom:city', Value: form.cityName },
			],
			// Read by the PreSignUp validator Lambda to resolve the Marketplace customer.
			ClientMetadata: { ...form, registrationToken: token },
		}),
	});
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw Object.assign(new Error(error.message), { type: error.__type });
	}
};

const errorMessage = (error) => {
	if (error.type === 'UsernameExistsException')
		return 'This email is already registered. Check your inbox for our onboarding email.';
	if (error.type === 'UserLambdaValidationException')
		return `We couldn't verify your AWS Marketplace subscription. Please contact ${SUPPORT_EMAIL}.`;
	if (error.type === 'InvalidParameterException') return error.message;
	return `Something went wrong. Please try again or contact ${SUPPORT_EMAIL}.`;
};

const Register = () => {
	const [token] = useState(() =>
		new URLSearchParams(window.location.search).get('x-amzn-marketplace-token')
	);
	const [form, setForm] = useState(EMPTY_FORM);
	const [status, setStatus] = useState('idle'); // idle | submitting | done
	const [error, setError] = useState('');

	const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

	const handleSubmit = async (e) => {
		e.preventDefault();
		setStatus('submitting');
		setError('');
		try {
			await signUp(
				Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()])),
				token
			);
			setStatus('done');
		} catch (err) {
			setError(errorMessage(err));
			setStatus('idle');
		}
	};

	let body;
	if (!token) {
		body = (
			<p className='registerMessage'>
				This page is for customers subscribing to eFlow through AWS Marketplace. Please
				open it from your AWS Marketplace subscription, or contact{' '}
				<a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
			</p>
		);
	} else if (status === 'done') {
		body = (
			<p className='registerMessage' role='status'>
				Thank you! Your registration was received. We've sent a confirmation to{' '}
				<strong>{form.email}</strong> with your next steps.
			</p>
		);
	} else {
		body = (
			<form className='registerForm' onSubmit={handleSubmit}>
				{FIELDS.map(({ name, label, ...input }) => (
					<label key={name} className='registerField'>
						<span>{label}</span>
						<input
							className='formInput'
							name={name}
							value={form[name]}
							onChange={handleChange}
							required
							{...input}
						/>
					</label>
				))}
				{error && (
					<p className='registerError' role='alert'>
						{error}
					</p>
				)}
				<button className='registerButton' type='submit' disabled={status === 'submitting'}>
					{status === 'submitting' ? 'Submitting…' : 'Complete registration'}
				</button>
			</form>
		);
	}

	return (
		<section className='registerContainer'>
			<div className='registerCard'>
				<h1>Welcome to eFlow</h1>
				<h2>Complete your AWS Marketplace registration</h2>
				{body}
			</div>
		</section>
	);
};

export default Register;
